import React from 'react'
import { useOutletContext } from 'react-router-dom'
import { revenueCodesAndNames, expenditureCodesAndNames, sumOfArray } from '../../../../helpers'

const Notesfs = ({currentYearData, previousYearData, startDate}) => {
    let count = 0;

    const {allData, setAllData} = useOutletContext();
    const currentYear = startDate ? new Date(startDate).getFullYear() : new Date().getFullYear();
    const previousYear = currentYear - 1;
  return (
    <div className='my-5'>
         <hr className="border-t-2 border-slate-200 py-5" />
         <h1 className="text-2xl font-bold text-slate-800">NOTES TO THE FINANCIAL STATEMENTS</h1>


         {revenueCodesAndNames.map((code) => {
            // Check if this code has any COA data
            const hasCoaData = allData.coa.some(data => data.account.startsWith(code.id));
            if (!hasCoaData) return null;
            
            // Check if all rows in this section have zero values for both years
            const allRowsZero = allData.coa
                .filter(data => data.account.startsWith(code.id))
                .every(data => {
                    const currentYearAmount = sumOfArray(
                        currentYearData
                            ?.filter(a => a?.coa && data?.account && String(a.coa) === String(data.account))
                            ?.map(a => parseFloat(a?.amount) || 0) || []
                    ) || 0;
                    
                    const previousYearAmount = sumOfArray(
                        previousYearData
                            ?.filter(a => a?.coa && data?.account && String(a.coa) === String(data.account))
                            ?.map(a => parseFloat(a?.amount) || 0) || []
                    ) || 0;
                    
                    return currentYearAmount === 0 && previousYearAmount === 0;
                });
            
            if (allRowsZero) return null;
            
            return (
                <div key={code.id} className="mb-8">
                    <h2 className='text-xl font-bold text-slate-800 mb-3'>{++count}. {code.name}</h2>
                    <p className='text-sm text-slate-700 mb-4'>During the year, the Council generated {(code.name).toLowerCase()} cash receipts as follows:</p>

                    <table className="w-full table-auto border-collapse shadow-sm rounded-lg overflow-hidden mb-6">
                        <thead className="bg-blue-950 text-white">
                            <tr>
                                <th className="text-left px-4 py-3 w-2/3">{code.name} Type</th>
                                <th className="text-right px-4 py-3 w-1/6">{currentYear}</th>
                                <th className="text-right px-4 py-3 w-1/6">{previousYear}</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white"> 
                            {allData.coa.map((data) => {
                                if(data.account.startsWith(code.id)){
                                    const currentYearAmount = sumOfArray(
                                        currentYearData
                                            ?.filter(a => a?.coa && data?.account && String(a.coa) === String(data.account))
                                            ?.map(a => parseFloat(a?.amount) || 0) || []
                                    ) || 0;
                                    
                                    const previousYearAmount = sumOfArray(
                                        previousYearData
                                            ?.filter(a => a?.coa && data?.account && String(a.coa) === String(data.account))
                                            ?.map(a => parseFloat(a?.amount) || 0) || []
                                    ) || 0;
                                    
                                    // Don't show row if both amounts are 0
                                    if (currentYearAmount === 0 && previousYearAmount === 0) return null;
                                    
                                    return (
                                        <tr key={data.account} className="odd:bg-gray-50 hover:bg-gray-100">
                                            <td className="px-4 py-3 text-slate-800 font-medium">{data.name}</td>
                                            <td className="px-4 py-3 text-right font-medium text-slate-800">{currentYearAmount.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-right font-medium text-slate-800">{previousYearAmount.toLocaleString()}</td>
                                        </tr>
                                    )
                                }
                                return null;
                            })}
                        </tbody>
                        <tfoot className="bg-gray-100">
                            <tr className="font-semibold border-t-2 border-gray-300">
                                <td className="px-4 py-3 text-slate-800">Total {code.name}</td>
                                <td className="px-4 py-3 text-right font-bold text-slate-800">{(() => {
                                    const total = allData.coa
                                        .filter(data => data.account.startsWith(code.id))
                                        .reduce((sum, data) => {
                                            const amount = sumOfArray(
                                                currentYearData
                                                    ?.filter(a => a?.coa && data?.account && String(a.coa) === String(data.account))
                                                    ?.map(a => parseFloat(a?.amount) || 0) || []
                                            ) || 0;
                                            return sum + amount;
                                        }, 0);
                                    return total.toLocaleString();
                                })()}</td>
                                <td className="px-4 py-3 text-right font-bold text-slate-800">{(() => {
                                    const total = allData.coa
                                        .filter(data => data.account.startsWith(code.id))
                                        .reduce((sum, data) => {
                                            const amount = sumOfArray(
                                                previousYearData
                                                    ?.filter(a => a?.coa && data?.account && String(a.coa) === String(data.account))
                                                    ?.map(a => parseFloat(a?.amount) || 0) || []
                                            ) || 0;
                                            return sum + amount;
                                        }, 0);
                                    return total.toLocaleString();
                                })()}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )
         })}
         
         {/* Expenditure Section */}
         <hr className="border-t-2 border-slate-200 my-8" />
         <h1 className="text-2xl font-bold text-slate-800 mb-6">EXPENDITURE NOTES</h1>
         
         {expenditureCodesAndNames.map((code) => {
            // Check if this code has any COA data
            const hasCoaData = allData.coa.some(data => data.account.startsWith(code.id));
            if (!hasCoaData) return null;
            
            // Check if all rows in this section have zero values for both years
            const allRowsZero = allData.coa
                .filter(data => data.account.startsWith(code.id))
                .every(data => {
                    const currentYearAmount = sumOfArray(
                        currentYearData
                            ?.filter(a => a?.coa && data?.account && String(a.coa) === String(data.account) && a.type === 'expense')
                            ?.map(a => parseFloat(a?.amount) || 0) || []
                    ) || 0;
                    
                    const previousYearAmount = sumOfArray(
                        previousYearData
                            ?.filter(a => a?.coa && data?.account && String(a.coa) === String(data.account) && a.type === 'expense')
                            ?.map(a => parseFloat(a?.amount) || 0) || []
                    ) || 0;
                    
                    return currentYearAmount === 0 && previousYearAmount === 0;
                });
            
            if (allRowsZero) return null;
            
            return (
                <div key={code.id} className="mb-8">
                    <h2 className='text-xl font-bold text-slate-800 mb-3'>{++count}. {code.name}</h2>
                    <p className='text-sm text-slate-700 mb-4'>During the year, the Council incurred {(code.name).toLowerCase()} expenses as follows:</p>

                    <table className="w-full table-auto border-collapse shadow-sm rounded-lg overflow-hidden mb-6">
                        <thead className="bg-red-950 text-white">
                            <tr>
                                <th className="text-left px-4 py-3 w-2/3">{code.name} Type</th>
                                <th className="text-right px-4 py-3 w-1/6">{currentYear}</th>
                                <th className="text-right px-4 py-3 w-1/6">{previousYear}</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white"> 
                            {allData.coa.map((data) => {
                                if(data.account.startsWith(code.id)){
                                    const currentYearAmount = sumOfArray(
                                        currentYearData
                                            ?.filter(a => a?.coa && data?.account && String(a.coa) === String(data.account) && a.type === 'expense')
                                            ?.map(a => parseFloat(a?.amount) || 0) || []
                                    ) || 0;
                                    
                                    const previousYearAmount = sumOfArray(
                                        previousYearData
                                            ?.filter(a => a?.coa && data?.account && String(a.coa) === String(data.account) && a.type === 'expense')
                                            ?.map(a => parseFloat(a?.amount) || 0) || []
                                    ) || 0;
                                    
                                    // Don't show row if both amounts are 0
                                    if (currentYearAmount === 0 && previousYearAmount === 0) return null;
                                    
                                    return (
                                        <tr key={data.account} className="odd:bg-gray-50 hover:bg-gray-100">
                                            <td className="px-4 py-3 text-slate-800 font-medium">{data.name}</td>
                                            <td className="px-4 py-3 text-right font-medium text-slate-800">{currentYearAmount.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-right font-medium text-slate-800">{previousYearAmount.toLocaleString()}</td>
                                        </tr>
                                    )
                                }
                                return null;
                            })}
                        </tbody>
                        <tfoot className="bg-red-50">
                            <tr className="font-semibold border-t-2 border-red-300">
                                <td className="px-4 py-3 text-slate-800">Total {code.name}</td>
                                <td className="px-4 py-3 text-right font-bold text-slate-800">{(() => {
                                    const total = allData.coa
                                        .filter(data => data.account.startsWith(code.id))
                                        .reduce((sum, data) => {
                                            const amount = sumOfArray(
                                                currentYearData
                                                    ?.filter(a => a?.coa && data?.account && String(a.coa) === String(data.account) && a.type === 'expense')
                                                    ?.map(a => parseFloat(a?.amount) || 0) || []
                                            ) || 0;
                                            return sum + amount;
                                        }, 0);
                                    return total.toLocaleString();
                                })()}</td>
                                <td className="px-4 py-3 text-right font-bold text-slate-800">{(() => {
                                    const total = allData.coa
                                        .filter(data => data.account.startsWith(code.id))
                                        .reduce((sum, data) => {
                                            const amount = sumOfArray(
                                                previousYearData
                                                    ?.filter(a => a?.coa && data?.account && String(a.coa) === String(data.account) && a.type === 'expense')
                                                    ?.map(a => parseFloat(a?.amount) || 0) || []
                                            ) || 0;
                                            return sum + amount;
                                        }, 0);
                                    return total.toLocaleString();
                                })()}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )
         })}
        
    </div>
  )
}

export default Notesfs